import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import { AppConfigService, navItems } from '@/config';
import { NavigationList } from './NavigationList';
import { motion } from 'framer-motion';
import Logo from '../../../public/logo_hoopcare.svg';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  userSelect: 'none',
  padding: theme.spacing(1, 2),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'space-between'
}));

const NavBackground = styled(motion.div)`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Version = styled('div')`
  margin: 12px 15px 4px;
  padding: 2px 12px;
  font-weight: 400;
  font-size: smaller;
  color: ${(p) => p.theme.palette.text.secondary};
  opacity: 0.7;
  flex-grow: 2;
  display: flex;
  align-items: flex-end;
`;

function useCloseOnRouteChange(closeAllowed: boolean, onClose: () => void) {
  const pathname = usePathname();
  const didMount = useRef(false);

  useEffect(() => {
    // Skip first render to mimic "routeChangeComplete" (only on navigations)
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (closeAllowed) {
      onClose();
    }
  }, [pathname, closeAllowed, onClose]);
}

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (p) => p !== 'keepMounted'
})``;

interface NavigationDrawerProps
  extends Omit<DrawerProps, 'children' | 'variant'> {
  drawerWidth: number;
  onClose: () => void;
  variant: Exclude<NonNullable<DrawerProps>['variant'], 'permanent'>;
}

export function NavigationDrawer({
  onClose,
  drawerWidth,
  variant,
  ...props
}: NavigationDrawerProps) {
  useCloseOnRouteChange(variant === 'temporary', onClose);

  return (
    <StyledDrawer
      keepMounted
      variant={variant}
      anchor="left"
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box'
        }
      }}
      {...props}
    >
      <nav>
        <NavBackground /*variants={sidebar}*/>
          <DrawerHeader>
            <Box width="fit-content" marginX="auto" justifyContent="center">
              <Image
                src={Logo}
                alt="HoopCare logo white"
                width={40}
                height={40}
                objectFit="contain"
                objectPosition="center"
              />
            </Box>
          </DrawerHeader>
          <NavigationList navItems={navItems} />
          <Version>v{AppConfigService.getInstance().appVersion}</Version>
        </NavBackground>
      </nav>
    </StyledDrawer>
  );
}
